import React, { useEffect, useState, useRef } from "react";
import "./InputandSelect.css";
import useAppConfigStore from "../../../store/useAppConfigStore";
import { useDropdownKeyboardNavigation } from "../../../hooks/useDropdownKeyboardNavigation";

const DiscountBox = ({
  options = [], // Array of {label, value} objects
  onChange,
  onSelectChange,
  className = "",
  defaultValue = "",
  defaultOption = "", // This should be the value, not the full option object
  selectDefaultValue = "", // Allow backwards compatibility with earlier usage
  readOnly = false,
  border = "",
  error = null,
  maxLength = null,
}) => {
  const [value, setValue] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    selectDefaultValue || defaultOption,
  );
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const decimalPlaces = useAppConfigStore((state) => state.decimalPlaces) ?? 2;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Helper function to find option by value
  const findOptionByValue = (value) => {
    return options.find((option) => option.value === value) || options[0];
  };

  // Helper function to get label for display
  const getDisplayLabel = (value) => {
    const option = findOptionByValue(value);
    return option ? option.label : value;
  };

  const handleSelect = (option) => {
    setSelectedOption(option.value); // Store only the value
    setIsOpen(false);

    const isPercentage =
      option.value === "percentage" ||
      option.value === "%" ||
      option.label === "%";

    if (isPercentage && value !== "") {
      const numericVal = parseFloat(value);
      if (!isNaN(numericVal) && numericVal > 100) {
        setValue("100");
        if (onChange) {
          onChange({
            target: {
              value: "100",
            },
          });
        }
      }
    } else if (!isPercentage && value !== "") {
      const numericVal = parseFloat(value);
      if (!isNaN(numericVal) && numericVal > 999999999999999.99) {
        setValue("999999999999999.99");
        if (onChange) {
          onChange({
            target: {
              value: "999999999999999.99",
            },
          });
        }
      }
    }

    if (onSelectChange) {
      const syntheticEvent = {
        target: {
          value: option.value, // Pass only the value, not the full option
        },
      };
      onSelectChange(syntheticEvent);
    }

    if (triggerRef.current && typeof triggerRef.current.focus === "function") {
      setTimeout(() => triggerRef.current.focus(), 0);
    }
  };

  const {
    highlightedIndex,
    handleTriggerKeyDown,
    handleItemMouseEnter,
  } = useDropdownKeyboardNavigation({
    isOpen,
    setIsOpen,
    options,
    onSelect: handleSelect,
    triggerRef,
    listRef,
    selectedValue: selectedOption,
    valueKey: "value",
  });

  useEffect(() => {
    if (!isFocused) {
      setValue(defaultValue);
    }
  }, [defaultValue, isFocused]);

  useEffect(() => {
    setSelectedOption(selectDefaultValue || defaultOption); // defaultOption should be the value
  }, [defaultOption, selectDefaultValue]);

  const handleInputChange = (event) => {
    let newValue = event.target.value;
    // Backend max_digits=15, decimal_places=4 -> max 11 integer digits before decimal
    const maxLen = maxLength !== null && maxLength !== undefined ? maxLength : 11 + 1 + decimalPlaces;

    if (maxLength !== null && maxLength !== undefined && newValue.length > maxLength) {
      return;
    }

    if (newValue === "") {
      setValue("");
      if (onChange) onChange(event);
      return;
    }

    const regex = new RegExp(`^\\d*(\\.\\d{0,${decimalPlaces}})?$`);

    // Allow only numbers with optional decimal point up to the configured decimalPlaces limit
    if (regex.test(newValue)) {
      const isPercentage =
        selectedOption === "percentage" ||
        selectedOption === "%" ||
        getDisplayLabel(selectedOption) === "%";

      if (isPercentage && newValue !== "") {
        const numericVal = parseFloat(newValue);
        if (!isNaN(numericVal) && numericVal > 100) {
          newValue = "100";
        }
      } else if (!isPercentage && newValue !== "") {
        const numericVal = parseFloat(newValue);
        if (!isNaN(numericVal) && numericVal > 99999999999.9999) {
          newValue = "99999999999.9999";
        }
      }

      if (newValue.length > maxLen) {
        return;
      }

      setValue(newValue);

      if (onChange) {
        const modifiedEvent = {
          ...event,
          target: {
            ...event.target,
            value: newValue,
          },
        };
        onChange(modifiedEvent);
      }
    }
  };

  return (
    <div
      ref={dropdownRef}
      className={`input-dropdown-container ${isOpen ? "is-open" : ""} ${error ? "has-error" : ""} ${className}`}
      title={error || ""}
    >
      <input
        value={value}
        readOnly={readOnly}
        onChange={handleInputChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`input-field common-text ${error ? "input-error" : ""} ${border}`}
        maxLength={maxLength !== null && maxLength !== undefined ? maxLength : undefined}
      />

      <div className="select-wrapper">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => !readOnly && setIsOpen(!isOpen)}
          onKeyDown={handleTriggerKeyDown}
          className={`select-button common-text ${error ? "input-error" : ""} ${border}`}
          style={{ cursor: readOnly ? "default" : "pointer" }}
        >
          {getDisplayLabel(selectedOption)} {/* Display label */}
          <span className="select-arrow" />
        </button>

        {isOpen && !readOnly && (
          <div className="dropdown-container" ref={listRef}>
            {options.map((option, idx) => {
              const isHighlighted = idx === highlightedIndex;
              return (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => handleItemMouseEnter(idx)}
                  className={`dropdown-option common-text ${
                    selectedOption === option.value ? "selected" : ""
                  } ${isHighlighted ? "highlighted" : ""}`}
                >
                  {option.label}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscountBox;
